import { getCharacter, type CharacterId } from '../content/characters';

export type MergeEntity = Readonly<{
  entityId: string;
  characterId: CharacterId;
  lifecycle: 'active' | 'staged' | 'consumed';
  mergeEligibleStep: number;
}>;

export type ContactCandidate = Readonly<{
  a: string;
  b: string;
  point: Readonly<{ x: number; y: number }>;
}>;

export type AcceptedMerge = Readonly<{
  a: string;
  b: string;
  generatedCharacterId: CharacterId;
  point: Readonly<{ x: number; y: number }>;
}>;

export function resolveMerges(
  entities: ReadonlyMap<string, MergeEntity>,
  contacts: readonly ContactCandidate[],
  step: number,
): readonly AcceptedMerge[] {
  const candidates = new Map<string, ContactCandidate>();
  for (const contact of contacts) {
    if (contact.a === contact.b) continue;
    const [a, b] = contact.a < contact.b ? [contact.a, contact.b] : [contact.b, contact.a];
    const key = `${a}\u0000${b}`;
    if (!candidates.has(key)) candidates.set(key, { ...contact, a, b });
  }

  const sorted = [...candidates.values()]
    .map((contact) => ({
      contact,
      stage: getCharacter(entities.get(contact.a)?.characterId ?? 'weak-stone').stage,
    }))
    .sort(
      (left, right) =>
        left.stage - right.stage ||
        left.contact.a.localeCompare(right.contact.a) ||
        left.contact.b.localeCompare(right.contact.b),
    );

  const reserved = new Set<string>();
  const accepted: AcceptedMerge[] = [];
  for (const { contact } of sorted) {
    const left = entities.get(contact.a);
    const right = entities.get(contact.b);
    if (!left || !right) continue;
    if (left.lifecycle !== 'active' || right.lifecycle !== 'active') continue;
    if (left.characterId !== right.characterId) continue;
    if (left.mergeEligibleStep > step || right.mergeEligibleStep > step) continue;
    if (reserved.has(left.entityId) || reserved.has(right.entityId)) continue;
    const generatedCharacterId = getCharacter(left.characterId).nextId;
    if (!generatedCharacterId) continue;
    reserved.add(left.entityId);
    reserved.add(right.entityId);
    accepted.push({
      a: left.entityId,
      b: right.entityId,
      generatedCharacterId,
      point: contact.point,
    });
  }
  return accepted;
}
